/**
 * LinkedPair - Two content slots connected by an icon/arrow.
 *
 * Used in association and merge action cards to show a source → target
 * relationship (or a symmetric link). Renders two content areas
 * side by side (stacked on mobile) with a connecting icon between them.
 */

import { ArrowRight, ArrowsRight } from '@untitledui/icons';
import { type ReactNode } from 'react';

import { UnlinkIcon } from '@/lib/proposals/registry/icons';
import { cx } from '@/utils/cx';

import { ArrowConnector } from './shared/ArrowConnector';

export interface LinkedPairProps {
  /** Source slot content */
  source: ReactNode;
  /** Target slot content */
  target: ReactNode;
  /**
   * Connecting icon between the two cards.
   * Defaults to an arrow (single or double depending on `direction`).
   */
  icon?: ReactNode;
  /** Optional label rendered below the source card (e.g., "Will be deleted") */
  sourceLabel?: string;
  /** Optional label rendered below the target card (e.g., "Will remain") */
  targetLabel?: string;
  /**
   * Arrow direction style.
   * - 'source-to-target' (default): single arrow →
   * - 'symmetric': double arrow ⇄
   */
  direction?: 'source-to-target' | 'symmetric';
  className?: string;
  /** Whether the pair is a removal */
  isRemoval?: boolean;
}

/**
 * LinkedPair - Side-by-side content slots with a directional connector.
 *
 * Used in action cards that describe a relationship between two entities
 * (associations, merges, etc.).
 */
export function LinkedPair({
  source,
  target,
  icon,
  sourceLabel,
  targetLabel,
  direction = 'source-to-target',
  className,
  isRemoval,
}: LinkedPairProps) {
  // Resolve the connecting icon
  let connectorIcon =
    icon ??
    (direction === 'symmetric' ? (
      <ArrowsRight className='w-4 h-4' />
    ) : (
      <ArrowRight className='w-4 h-4' />
    ));

  if (isRemoval) {
    connectorIcon = <UnlinkIcon className='w-4 h-4' />;
  }

  // On sm+ the three slots sit in a row. Making both source and target
  // `flex-1` with their content left-aligned causes the source chip to
  // hug its left edge (big gap before the connector) while the target
  // chip sits flush against the connector on the other side. Aligning
  // the source slot's chip to its right edge (`sm:items-end`) mirrors
  // that so both chips sit the same distance from the connector — equal
  // breathing room on both sides.
  //
  // The inner chip wrapper keeps `min-w-0 sm:max-w-full` so long labels
  // truncate within the parent's width instead of overflowing the row
  // (compact previews rely on a bounded width for their `truncate`).
  //
  // On <sm the layout stacks vertically, so we keep default
  // (start-aligned) alignment: it's already symmetric top-to-bottom.
  return (
    <div
      className={cx(
        'flex flex-col sm:flex-row sm:items-center gap-6',
        className
      )}
    >
      {/* Source */}
      <div className='flex flex-col gap-1 flex-1 min-w-0 sm:items-end'>
        <div className='min-w-0 max-w-full'>{source}</div>
        {sourceLabel && (
          <span className='text-xs text-fg-tertiary px-1'>{sourceLabel}</span>
        )}
      </div>

      {/* Connector */}
      <div className='flex sm:flex-col items-center justify-center shrink-0 py-1 sm:py-0 sm:px-1'>
        <ArrowConnector responsiveRotation>{connectorIcon}</ArrowConnector>
      </div>

      {/* Target */}
      <div className='flex flex-col gap-1 flex-1 min-w-0 sm:items-start'>
        <div className='min-w-0 max-w-full'>{target}</div>
        {targetLabel && (
          <span className='text-xs text-fg-tertiary px-1'>{targetLabel}</span>
        )}
      </div>
    </div>
  );
}
